import { useUser } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useTRPC } from "@/frontend/lib/trpc";

// QUERIES
export function useProjectsQuery() {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.project.list.queryOptions(),
    enabled: !!user,
  });
}

export function useProjectQuery(projectId: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.project.get.queryOptions(projectId),
    enabled: !!user,
  });
}

export function useProjectFilesQuery(projectId: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.project.getFiles.queryOptions(projectId),
    enabled: !!user,
  });
}

export function useProjectJamsQuery(projectId: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.project.getJams.queryOptions(projectId),
    enabled: !!user,
  });
}

export function useProjectFileDownloadUrlQuery(fileId: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.project.getFileDownloadUrl.queryOptions(fileId),
    enabled: !!user,
  });
}

// MUTATIONS
export function useCreateProject() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.project.create.mutationOptions(),
    onSuccess: async (newProject) => {
      await queryClient.cancelQueries(trpc.project.list.queryFilter());
      queryClient.setQueryData(trpc.project.list.queryKey(), (old) => {
        if (!old) return old;
        return [
          ...old,
          {
            ...newProject,
            jamCount: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            deleted: false,
            description: newProject.description ?? null,
            instructions: newProject.instructions ?? null,
          },
        ];
      });
      await queryClient.invalidateQueries(trpc.project.list.queryFilter());
    },
  });
}

export function useUpdateProject() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.project.update.mutationOptions(),
    onMutate: async ({ id, name, description, instructions }) => {
      await queryClient.cancelQueries(trpc.project.get.queryFilter(id));
      queryClient.setQueryData(trpc.project.get.queryKey(id), (old) => {
        if (!old) return old;
        return {
          ...old,
          name: name ?? old.name,
          description: description ?? old.description,
          instructions: instructions ?? old.instructions,
        };
      });
      return undefined;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(
        trpc.project.get.queryFilter(variables.id),
      );
      await queryClient.invalidateQueries(trpc.project.list.queryFilter());
    },
  });
}

export function useDeleteProject() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.project.delete.mutationOptions(),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(trpc.project.list.queryFilter());
      queryClient.setQueryData(trpc.project.list.queryKey(), (old) => {
        if (!old) return old;
        return old.filter((project) => project.id !== id);
      });
      return undefined;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.project.list.queryFilter());
      await queryClient.invalidateQueries(trpc.jam.list.infiniteQueryFilter());
    },
  });
}

// First, create a separate hook for the basic upload mutation
export function useUploadProjectFileMutation() {
  const trpc = useTRPC();
  return useMutation(trpc.project.uploadFile.mutationOptions());
}

// Then create the wrapper that handles multiple files and S3 uploads
export function useUploadProjectFile() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const uploadMutation = useUploadProjectFileMutation();

  return useMutation({
    mutationFn: async ({
      projectId,
      files,
    }: {
      projectId: string;
      files: File[];
    }) => {
      const uploadPromises = files.map(async (file) => {
        try {
          // First, get the presigned upload URL from TRPC
          const { uploadUrl } = await uploadMutation.mutateAsync({
            projectId,
            filename: file.name,
            size: file.size,
          });

          // Then upload directly to S3
          const uploadResponse = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
          });

          if (!uploadResponse.ok) {
            throw new Error("Failed to upload file");
          }

          return { success: true, filename: file.name };
        } catch (error) {
          return {
            success: false,
            filename: file.name,
            error: error instanceof Error ? error.message : "Upload failed",
          };
        }
      });

      const results = await Promise.all(uploadPromises);
      const failures = results.filter((r) => !r.success);

      if (failures.length > 0) {
        throw new Error(
          `Failed to upload ${failures.length} file${
            failures.length > 1 ? "s" : ""
          }`,
        );
      }

      return results;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(
        trpc.project.getFiles.queryFilter(variables.projectId),
      );
    },
  });
}

export function useDeleteProjectFile() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.project.deleteFile.mutationOptions(),
    onMutate: async ({ projectId, fileId }) => {
      await queryClient.cancelQueries(
        trpc.project.getFiles.queryFilter(projectId),
      );
      queryClient.setQueryData(
        trpc.project.getFiles.queryKey(projectId),
        (old) => {
          if (!old) return old;
          return old.filter((file) => file.id !== fileId);
        },
      );
      return undefined;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(
        trpc.project.getFiles.queryFilter(variables.projectId),
      );
    },
  });
}
